import { useEffect, useMemo } from 'react';
import { useGlobal, usePostCode } from '../../hooks';
import { useFetchInfiniteContent } from './useFetchContent';
import { useContentLists } from '../ContentProvider';
import { FeaturedContent } from 'rwl-shared/dist/models';

const processContent = (pages: FeaturedContent[]) => {
    const jobsFromAllPages = pages.map((p) => p.jobs).flat();
    const similarJobsFromAllPages = pages.map((p) => p.similarJobs).flat();
    const reviewsFromAllPages = pages.map((p) => p.reviews).flat();
    const similarReviewsFromAllPages = pages
        .map((p) => p.similarReviews)
        .flat();

    return {
        jobs: jobsFromAllPages,
        similarJobs: similarJobsFromAllPages,
        reviews: reviewsFromAllPages,
        similarReviews: similarReviewsFromAllPages,
    };
};

export const ContentFetcher = (): null => {
    const { accountId, isReady } = useGlobal();
    const postCode = usePostCode();
    const {
        setJobList,
        setSimilarJobList,
        setReviewList,
        setSimilarReviewList,
        setTotalJobs,
        setTotalReviews,
        setHasMoreJobs,
        setHasMoreReviews,
        setFetchNextPage,
        setIsLoading,
    } = useContentLists();

    const { data, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage } =
        useFetchInfiniteContent({
            accountId,
            enabled: isReady,
            postCode,
        });

    // Memoize processed content
    const content = useMemo(() => {
        if (!data?.pages?.length) return null;
        return processContent(data.pages);
    }, [data?.pages, processContent]);

    // Update content lists
    useEffect(() => {
        if (!content) return;

        setJobList(content.jobs);
        setSimilarJobList(content.similarJobs);
        setReviewList(content.reviews);
        setSimilarReviewList(content.similarReviews);

        // Update totals from first page
        const firstPage = data?.pages[0];
        if (firstPage) {
            setTotalJobs(firstPage.jobs?.length || 0);
            setTotalReviews(firstPage.reviews?.length || 0);
        }
    }, [content, data?.pages]);

    // Update pagination state
    useEffect(() => {
        setHasMoreJobs(!!hasNextPage);
        setHasMoreReviews(!!hasNextPage);
        setFetchNextPage(fetchNextPage);
    }, [
        hasNextPage,
        fetchNextPage,
        setHasMoreJobs,
        setHasMoreReviews,
        setFetchNextPage,
    ]);

    // Update loading state
    useEffect(() => {
        setIsLoading(isLoading || (isFetchingNextPage && !!hasNextPage));
    }, [isLoading, isFetchingNextPage, hasNextPage, setIsLoading]);

    return null;
};
